import { cloneStructured } from './structured-clone'

/**
 * The wire between a host window and its replica. Both are windows of the
 * same origin in the same browser, so a `BroadcastChannel` reaches the other
 * one without either holding a reference to the other, and keeps doing so
 * across a reload of either side.
 */
export type ReplicationChannel = {
	/** Throws `DataCloneError` synchronously when `message` cannot be cloned. */
	post: (message: unknown) => void
	subscribe: (listener: (data: unknown) => void) => () => void
	close: () => void
}

export const openBroadcastChannel = (name: string): ReplicationChannel => {
	const channel = new BroadcastChannel(name)
	const listeners = new Set<(data: unknown) => void>()
	channel.onmessage = (event: MessageEvent<unknown>) =>
		listeners.forEach(listener => listener(event.data))
	return {
		post: message => channel.postMessage(message),
		subscribe: listener => {
			listeners.add(listener)
			return () => listeners.delete(listener)
		},
		close: () => {
			listeners.clear()
			channel.close()
		},
	}
}

/**
 * Two ends of an in-memory channel, for tests. Like a `BroadcastChannel`, a
 * message is cloned and delivered asynchronously, to the other end only - in
 * a microtask, so fake timers leave it alone.
 */
export const memoryChannelPair = (): [
	ReplicationChannel,
	ReplicationChannel
] => {
	type End = { listeners: Set<(data: unknown) => void>; closed: boolean }
	const ends: [End, End] = [
		{ listeners: new Set(), closed: false },
		{ listeners: new Set(), closed: false },
	]
	const make = (self: End, other: End): ReplicationChannel => ({
		post: message => {
			const copy = cloneStructured(message)
			queueMicrotask(() => {
				if (!self.closed && !other.closed) {
					other.listeners.forEach(listener => listener(copy))
				}
			})
		},
		subscribe: listener => {
			self.listeners.add(listener)
			return () => self.listeners.delete(listener)
		},
		close: () => {
			self.closed = true
			self.listeners.clear()
		},
	})
	return [make(ends[0], ends[1]), make(ends[1], ends[0])]
}
